import { useContext } from 'react'

import { ModalContext, SidebarModal } from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import useCampaignQuote from '@hooks/useCampaignQuote'
import useCurrentUser from '@hooks/useCurrentUser'

import type { CampaignQuoteModel } from '@models/types'

import CopyFromCampaign from './screens/CopyFromCampaign'
import CreateFromTemplate from './screens/CreateFromTemplate'
import DefaultScreen from './screens/DefaultScreen'

const MODAL_KEY = 'NewCampaignQuoteModal'

const modalTitles = {
  CopyFromCampaign: {
    title: 'Copy Quote',
    subtitle: 'from Campaign',
  },
  CreateFromTemplate: {
    title: 'Create Quote',
    subtitle: 'from Template',
  },
}

export type DefaultState = {
  selectedScreen: 'CopyFromCampaign' | 'CreateFromTemplate' | undefined,
}

const defaultState: DefaultState = {
  selectedScreen: undefined,
}

type ModalCallbacks = {
  afterAction: VoidFunction,
  closeModal: VoidFunction,
  createCampaignQuote: VoidFunction,
  createQuoteFromExisting: VoidFunction,
  createQuoteFromTemplate: VoidFunction,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  campaignQuote: Partial<CampaignQuoteModel>,
  requestOptions?: object,
  showModal: boolean,
}

const NewCampaignQuoteModal = (props: ModalProps) => {
  const {
    callbacks,
    callbacks: {
      afterAction,
      closeModal,
      createCampaignQuote,
      createQuoteFromExisting,
      createQuoteFromTemplate,
    },
    campaignQuote,
    requestOptions,
    showModal,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { selectedScreen } = state

  const campaignQuotePayload = useCampaignQuote(campaignQuote)
  const currentUserPayload = useCurrentUser()

  const modalTitle = selectedScreen && modalTitles[selectedScreen] ? modalTitles[selectedScreen] : {}

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        // @ts-expect-error TS2339
        title={modalTitle.title || 'New'}
        // @ts-expect-error TS2339
        titleSecondLine={modalTitle.subtitle || 'Quote'}
      />

      {!selectedScreen && (
        <DefaultScreen
          callbacks={{
            afterAction,
            closeModal,
            createCampaignQuote,
            selectScreen: (screenKey: DefaultState['selectedScreen']) => setState({ selectedScreen: screenKey }),
          }}
          campaignQuotePayload={campaignQuotePayload}
          requestOptions={requestOptions}
        />
      )}

      {selectedScreen === 'CopyFromCampaign' && (
        <CopyFromCampaign
          callbacks={{
            afterAction,
            closeModal,
            createQuoteFromExisting,
          }}
          campaignQuotePayload={campaignQuotePayload}
          currentUserPayload={currentUserPayload}
          requestOptions={requestOptions}
        />
      )}

      {selectedScreen === 'CreateFromTemplate' && (
        <CreateFromTemplate
          callbacks={{
            afterAction,
            closeModal,
            createQuoteFromTemplate,
          }}
          campaignQuotePayload={campaignQuotePayload}
          currentUserPayload={currentUserPayload}
          requestOptions={requestOptions}
        />
      )}

    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <NewCampaignQuoteModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
